import _ from "lodash";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { getValueBasedOnReportType } from "./reportTypeHelpers";
import {
  canOverrideScoreInReports,
  getIsEnableFinalGradeForIL,
  getIsOptimisedProgressReportGetRating,
} from "./featureFlags";

const getRatingAcademicCriteriaValue = ({
  curriculumType,
  criteriaSet = {},
  rating,
  localGradeCalculationMethod,
}) => {
  // For MYP localGrade when it is calculated from final grade we check "local_grade_value_id"
  // As in this case there are different local grades across grades
  if (
    curriculumType == CURRICULUM_TYPE_MYP &&
    criteriaSet.criteriaType == "LOCAL_GRADE" &&
    localGradeCalculationMethod == "MYP_FINAL_GRADE" &&
    canOverrideScoreInReports()
  ) {
    return _.find(criteriaSet.academicCriteriaValues, {
      local_grade_value_id: rating.academicCriteriaValue,
    });
  } else {
    return _.find(criteriaSet.academicCriteriaValues, {
      id: rating.academicCriteriaValue,
    });
  }
};

const getRatingOriginal = ({
  computedRating = false,
  ratings,
  overallGradeSet,
  customOverallGradeSets,
  subjectId,
  categoryId,
  globalCategoryId,
  skipIsRemoved = false,
  gradingPeriodType,
  termId,
  curriculumType,
  localGradeCalculationMethod,
  isProjectCriteria = false,
}) => {
  const updatedRatings = _.cloneDeep(ratings);
  return _.find(updatedRatings, item => {
    const academicCriteriaValue = getRatingAcademicCriteriaValue({
      curriculumType,
      criteriaSet: overallGradeSet,
      rating: item,
      localGradeCalculationMethod,
    });

    // For UBD local grade and  ASSESSMENT_CATEGORY, always rating.value is used
    if (
      !(
        curriculumType === CURRICULUM_TYPE_UBD &&
        overallGradeSet?.criteriaType == "LOCAL_GRADE"
      ) &&
      overallGradeSet?.criteriaType != "ASSESSMENT_CATEGORY"
    ) {
      item.value = _.get(academicCriteriaValue, "label") || item.value;
    }

    const isMatchingCriteriaSet = customOverallGradeSets
      ? _.includes(customOverallGradeSets, item.academicCriteriaSet)
      : item.academicCriteriaSet == overallGradeSet?.id;

    const isMatchingSubject =
      getValueBasedOnReportType() &&
      !isProjectCriteria &&
      _.has(item, "contextId")
        ? item.contextId == subjectId
        : item.ibPYPElementId == subjectId;

    const isMatchingCategory = getValueBasedOnReportType()
      ? _.get(item, "globalCategoryId", null) == globalCategoryId
      : _.get(item, "categoryId", null) == categoryId;

    const isMatchingComputedRating = _.has(item, "isComputedRating")
      ? item.isComputedRating == computedRating
      : true;

    const hasAcademicTermId = _.get(item, "academicTermId", null)
      ? item.academicTermId
      : true;

    const isNotRemoved =
      computedRating && !skipIsRemoved && _.has(item, "isRemoved")
        ? !item.isRemoved
        : true;

    const isMatchingTerm =
      gradingPeriodType == "CUMULATIVE" && !isProjectCriteria
        ? item.academicTermId == termId
        : true;

    return (
      isMatchingCriteriaSet &&
      isMatchingSubject &&
      isMatchingCategory &&
      isMatchingComputedRating &&
      hasAcademicTermId &&
      !_.isNull(item.value) &&
      isNotRemoved &&
      isMatchingTerm
    );
  });
};

const getRatingOptimised = ({
  computedRating = false,
  ratings,
  overallGradeSet,
  customOverallGradeSets,
  subjectId,
  categoryId,
  globalCategoryId,
  skipIsRemoved = false,
  gradingPeriodType,
  termId,
  curriculumType,
  localGradeCalculationMethod,
  isProjectCriteria = false,
}) => {
  // Hoist loop-invariant computations
  const isValueBasedOnReportType = getValueBasedOnReportType();
  const isCumulative = gradingPeriodType == "CUMULATIVE";
  // For UBD local grade and ASSESSMENT_CATEGORY, always rating.value is used
  const shouldUpdateValue =
    !(
      curriculumType === CURRICULUM_TYPE_UBD &&
      overallGradeSet?.criteriaType == "LOCAL_GRADE"
    ) && overallGradeSet?.criteriaType != "ASSESSMENT_CATEGORY";

  const match = _.find(ratings, item => {
    const isMatchingCriteriaSet = customOverallGradeSets
      ? _.includes(customOverallGradeSets, item.academicCriteriaSet)
      : item.academicCriteriaSet == overallGradeSet?.id;
    if (!isMatchingCriteriaSet) return false;

    const isMatchingSubject =
      isValueBasedOnReportType && !isProjectCriteria && "contextId" in item
        ? item.contextId == subjectId
        : item.ibPYPElementId == subjectId;
    if (!isMatchingSubject) return false;

    const isMatchingCategory = isValueBasedOnReportType
      ? (item.globalCategoryId ?? null) == globalCategoryId
      : (item.categoryId ?? null) == categoryId;
    if (!isMatchingCategory) return false;

    if ("isComputedRating" in item && item.isComputedRating != computedRating) {
      return false;
    }

    // Check value is not null (accounting for potential label override)
    if (
      item.value === null &&
      (!shouldUpdateValue ||
        !getRatingAcademicCriteriaValue({
          curriculumType,
          criteriaSet: overallGradeSet,
          rating: item,
          localGradeCalculationMethod,
        })?.label)
    ) {
      return false;
    }

    const isNotRemoved = !(
      computedRating &&
      !skipIsRemoved &&
      "isRemoved" in item &&
      item.isRemoved
    );

    return (
      isNotRemoved &&
      (!isCumulative || isProjectCriteria || item.academicTermId == termId)
    );
  });

  if (!match) return undefined;

  // Shallow clone only the matched item instead of deep-cloning the entire array
  if (shouldUpdateValue) {
    const academicCriteriaValue = getRatingAcademicCriteriaValue({
      curriculumType,
      criteriaSet: overallGradeSet,
      rating: match,
      localGradeCalculationMethod,
    });
    const label = academicCriteriaValue?.label;
    if (label) {
      return { ...match, value: label };
    }
  }

  return { ...match };
};

export const getRating = params =>
  getIsOptimisedProgressReportGetRating()
    ? getRatingOptimised(params)
    : getRatingOriginal(params);

// This common fn is used to find the matching rating for gpa associated ratings (gpRatings, credits, qualityPoints and gpaRatings)
// Since gpa is not defined at a single subject level (it's calculated based on multiple subjects), so in that case subjectId is not passed

export const getGpaAssociatedDataRating = ({
  ratings,
  subjectId,
  termId,
  genericTagId,
  defineDifferentCredits = false,
  shouldAddAssociatedLevelCheck = false,
}) => {
  return _.find(ratings, item => {
    const entityId = getValueBasedOnReportType({
      courseValue: _.get(
        item,
        "entityId",
        _.get(item, "learningCourseId", null)
      ),
      subjectValue: _.get(item, "entityId", _.get(item, "subjectId", null)),
    });
    const entityIdMatchesSubject = entityId ? entityId === subjectId : true;
    const courseMatch = _.has(item, "learningCourse")
      ? item.learningCourse?.id === subjectId
      : entityIdMatchesSubject;
    const subjectMatch = _.has(item, "subject")
      ? item.subject?.id === subjectId
      : entityIdMatchesSubject;

    const itemTagId = _.has(item, "genericTagId")
      ? item.genericTagId
      : item?.genericTag?.id;
    const associatedLevelMatch =
      defineDifferentCredits && genericTagId
        ? itemTagId === genericTagId
        : _.isNil(itemTagId);
    return (
      getValueBasedOnReportType({
        courseValue: courseMatch,
        subjectValue: subjectMatch,
        defaultValue: item.subjectId ? item.subjectId === subjectId : true,
      }) &&
      (_.has(item, "academicTermId")
        ? item.academicTermId === termId
        : item.academicTerm?.id === termId) &&
      (shouldAddAssociatedLevelCheck ? associatedLevelMatch : true)
    );
  });
};

const getCategoryGradeEquivalent = ({
  academicCriteriaSets,
  ratings,
  criteriaSet,
  curriculumType,
  subjectId,
  globalCategoryId,
  subjectType,
  overallGradeSet,
  customOverallGradeSets,
  categoryId = null,
  gradingPeriodType,
  termId,
}) => {
  let gradeBoundaryValue = "";
  if (_.isEqual(criteriaSet.criteriaType, "ASSESSMENT_CATEGORY")) {
    if (curriculumType == CURRICULUM_TYPE_DP) {
      const gradeSet = _.find(academicCriteriaSets, {
        criteriaType: "FINAL_GRADE",
        subType: subjectType == "TOK" ? "TOK" : "SUBJECT",
      });
      const matchedRating = _.find(
        ratings,
        item =>
          item.academicCriteriaSet == _.get(gradeSet, "id", "") &&
          item.ibPYPElementId == subjectId &&
          item.categoryId == criteriaSet.id &&
          !!item.isComputedRating &&
          item.academicTermId &&
          !_.isNull(item.value)
      );
      gradeBoundaryValue = _.get(matchedRating, "value", "-");
    } else {
      const gradeSet =
        curriculumType == CURRICULUM_TYPE_UBD
          ? _.find(academicCriteriaSets, {
              criteriaType: "LOCAL_GRADE",
              subType: "DEFAULT",
            })
          : _.find(academicCriteriaSets, {
              criteriaType: "FINAL_GRADE",
              subType: "SUBJECT",
            });
      const matchedRating = getRating({
        computedRating: true,
        ratings,
        overallGradeSet: gradeSet,
        subjectId,
        categoryId: criteriaSet.id,
        skipIsRemoved: true,
      });
      gradeBoundaryValue = _.get(matchedRating, "value", "-");
    }
  } else {
    const rating = getRating({
      computedRating: true,
      ratings,
      overallGradeSet,
      customOverallGradeSets,
      subjectId,
      globalCategoryId,
      categoryId,
      skipIsRemoved: true,
      gradingPeriodType,
      termId,
    });
    gradeBoundaryValue = _.get(rating, "value", "-");
  }
  return gradeBoundaryValue;
};

export const getOverallRating = ({
  ratings,
  subjectId,
  categoryId,
  globalCategoryId,
  overallGradeSet,
  // Use this when rating can have multiple criteria sets
  customOverallGradeSets,
  criteriaSet,
  curriculumType,
  subjectType,
  academicCriteriaSets,
  gradingPeriodType,
  termId,
}) => {
  const editedRating = getRating({
    computedRating: false,
    ratings,
    overallGradeSet,
    customOverallGradeSets,
    subjectId,
    categoryId,
    globalCategoryId,
    gradingPeriodType,
    termId,
    curriculumType,
  });

  const computedRating = getRating({
    computedRating: true,
    ratings,
    overallGradeSet,
    customOverallGradeSets,
    subjectId,
    categoryId,
    globalCategoryId,
    gradingPeriodType,
    termId,
    curriculumType,
  });

  //So to compare we checking without isRemoved condition

  const calculatedGrade = getCategoryGradeEquivalent({
    academicCriteriaSets,
    ratings,
    criteriaSet,
    curriculumType,
    subjectId,
    globalCategoryId,
    subjectType,
    overallGradeSet,
    customOverallGradeSets,
    categoryId,
    gradingPeriodType,
    termId,
  });

  const rating = editedRating ? editedRating : computedRating;

  // If isRemoved is true, we are setting score (whether a final score or a category score) as '-'
  if (_.get(rating, "isRemoved")) {
    rating.value = "-";
  }

  const showWarning =
    !_.isEmpty(calculatedGrade) &&
    calculatedGrade != _.get(rating, "value", "-");

  return { rating, showWarning, calculatedGrade };
};

export const getFormattedGpaAssociatedRatingValue = value => {
  if (value != "-") {
    let formattedValue = _.round(value, 2); // Round to 2 decimal places

    if (formattedValue % 1 === 0) {
      // Ensure atleast 1 decimal place (Eg: 4.0 instead of 4)
      formattedValue = formattedValue.toFixed(1);
    }

    return formattedValue;
  }
  return value;
};

export const getUpdatedCriteriaValuesForMypFinalGrade = ({
  academicCriteriaSets,
  academicCriteriaValues,
  uid,
}) => {
  const iLFinalGradeCriteriaSet = _.find(academicCriteriaSets, {
    criteriaType: "IL_FINAL_GRADE",
  });

  //Since IL has different grade boundaries we will use boundaries from IL_FINAL_GRADE criteria.
  return _.includes(
    ["IL_GRADE_DESCRIPTOR", "INTERDISCIPLINARY_OBJECTIVES"],
    uid
  ) && getIsEnableFinalGradeForIL()
    ? _.map(academicCriteriaValues, value => ({
        ...value,
        boundary_guidelines: _.get(
          _.find(iLFinalGradeCriteriaSet?.academicCriteriaValues, {
            abbreviation: value?.abbreviation,
          }),
          "boundary_guidelines",
          []
        ),
      }))
    : academicCriteriaValues;
};

export const notExistsInScoreAggregationConfiguration = ({
  scoreAggregationConfiguration,
  subjectId,
  academicCriteriaSetId,
  academicTermId,
  isNewGradeScaleSettingsEnabled,
}) => {
  if (isNewGradeScaleSettingsEnabled) {
    return !_.some(scoreAggregationConfiguration, config => {
      const itemId = getValueBasedOnReportType({
        courseValue: _.has(config, "learningCourseId")
          ? config?.learningCourseId
          : config?.learningCourse?.id,
        subjectValue: _.has(config, "subjectId")
          ? config?.subjectId
          : config?.subject?.id,
      });
      const criteriaId = config?.gradeScaleId;
      const termId = _.has(config, "academicTermId")
        ? config.academicTermId
        : config?.academicTerm?.id;
      return (
        subjectId === itemId &&
        criteriaId === academicCriteriaSetId &&
        academicTermId === termId
      );
    });
  }
  return !_.some(scoreAggregationConfiguration, {
    subjectId,
    academicCriteriaSetId,
    academicTermId,
  });
};

export const getShowAsteriskOnRating = ({
  isScoreAggregationEnabled,
  gradingPeriodType,
  isScoreAndGradeAggregationEnabled,
  scoreAggregationConfiguration,
  subjectId,
  academicCriteriaSetId,
  academicTermId,
  showAsterisk,
  isNewGradeScaleSettingsEnabled,
  criteriaType,
}) => {
  const isNewScoreAggregationEnabled =
    isScoreAndGradeAggregationEnabled && showAsterisk;

  const isPreviousScoreAggregationEnabled =
    isScoreAggregationEnabled &&
    gradingPeriodType == "CUMULATIVE" &&
    !isScoreAndGradeAggregationEnabled;

  if (isNewScoreAggregationEnabled || isPreviousScoreAggregationEnabled) {
    if (isNewGradeScaleSettingsEnabled) {
      // for final score academicCriteriaSetId is not resolved in BE so for that case pass null
      // final score rating existence will be determined based on scoreAggregationConfiguration rating values with gradingType set as SCORE
      return notExistsInScoreAggregationConfiguration({
        scoreAggregationConfiguration,
        subjectId,
        academicCriteriaSetId:
          criteriaType === "FINAL_SCORE" ? null : academicCriteriaSetId,
        academicTermId,
        isNewGradeScaleSettingsEnabled,
      });
    }
    return notExistsInScoreAggregationConfiguration({
      scoreAggregationConfiguration,
      subjectId,
      academicCriteriaSetId,
      academicTermId,
    });
  }

  return false;
};

const getEntityId = ({ criteriaSet, entityType }) => {
  const parentEntities = _.get(criteriaSet, "parentEntities", []);
  const entity = _.find(parentEntities, { entityType });
  return _.get(entity, "entityId", "");
};

const getSubjectTermAndGradeEntityIdFromParentEntities = ({ criteriaSet }) => {
  const subjectEntityId = getEntityId({ criteriaSet, entityType: "SUBJECT" });
  const termEntityId = getEntityId({
    criteriaSet,
    entityType: "ACADEMIC_TERM",
  });
  const gradeEntityId = getEntityId({ criteriaSet, entityType: "GRADE" });
  return { subjectEntityId, termEntityId, gradeEntityId };
};

const getDefaultLocalGradeSet = ({ academicCriteriaSets }) => {
  return _.find(
    academicCriteriaSets,
    criteriaSet =>
      criteriaSet.criteriaType == "LOCAL_GRADE" &&
      criteriaSet.subType == "DEFAULT"
  );
};

const getSubjectCustomLocalGradeSet = ({
  academicCriteriaSets,
  subjectId,
  termId,
  gradingPeriodType,
}) => {
  return _.find(academicCriteriaSets, criteriaSet => {
    if (
      criteriaSet.criteriaType != "LOCAL_GRADE" &&
      criteriaSet.subType != "CUSTOM"
    ) {
      return false;
    }

    const {
      subjectEntityId,
      termEntityId,
    } = getSubjectTermAndGradeEntityIdFromParentEntities({ criteriaSet });

    return (
      subjectEntityId == subjectId &&
      (gradingPeriodType == "CUMULATIVE" ? termId == termEntityId : true)
    );
  });
};

export const getUBDLocalGradeSet = ({
  academicCriteriaSets,
  subjectId,
  termId,
  gradingPeriodType,
}) => {
  const defaultLocalGrade = getDefaultLocalGradeSet({ academicCriteriaSets });

  const customLocalGrade = getSubjectCustomLocalGradeSet({
    academicCriteriaSets,
    subjectId,
    termId,
    gradingPeriodType,
  });

  return _.isEmpty(customLocalGrade) ? defaultLocalGrade : customLocalGrade;
};

export const getCustomAcademicCriteriaValues = ({
  academicCriteriaSets,
  subjectId,
  curriculumType,
  termId,
  gradingPeriodType,
  criteriaSet,
  gradeId,
  localGradeCalculationMethod,
}) => {
  if (curriculumType == CURRICULUM_TYPE_UBD) {
    const localGradeCriteriaSet = getUBDLocalGradeSet({
      academicCriteriaSets,
      subjectId,
      termId,
      gradingPeriodType,
    });

    return _.get(localGradeCriteriaSet, "academicCriteriaValues", []);
  } else if (
    curriculumType == CURRICULUM_TYPE_MYP &&
    localGradeCalculationMethod == "MYP_FINAL_GRADE"
  ) {
    const academicCriteriaValues = _.get(
      criteriaSet,
      "academicCriteriaValues",
      []
    );

    return _.filter(academicCriteriaValues, item => item.grade_id == gradeId);
  }
  return null;
};

const ELEMENT_TYPE_WITHOUT_TERM_ID_IN_RATING = [
  "MYP_OBJECTIVES",
  "MYP_ATL",
  "MYP_LEARNER_PROFILE",
  "MYP_SERVICE_ACTION_OUTCOME",
];

export const getStudentElementRatingOfCriteriaSet = ({
  ratings,
  type,
  itemId,
  setId,
  nodeId,
  isComputedRating = false,
  gradingPeriodType,
  termId,
  checkCategoryId = false,
  categoryId,
  shouldCheckComputedRating = true,
  globalCategoryId,
}) => {
  return _.find(ratings, ratingObj => {
    let isMatched = ratingObj.ibPYPElementType == type;
    const computedRatingCheck =
      _.has(ratingObj, "isComputedRating") && shouldCheckComputedRating
        ? ratingObj.isComputedRating == isComputedRating
        : true;
    if (itemId) {
      isMatched = ratingObj.ibPYPElementId == itemId && isMatched;
    }
    let isTermIdMatched = true;
    if (gradingPeriodType === "CUMULATIVE") {
      isTermIdMatched =
        ratingObj.academicTermId === termId ||
        (!termId &&
          !ratingObj.academicTermId &&
          _.includes(ELEMENT_TYPE_WITHOUT_TERM_ID_IN_RATING, type));
    }
    let isCategoryIdMatched = true;
    if (checkCategoryId && _.has(ratingObj, "categoryId")) {
      isCategoryIdMatched = ratingObj.categoryId === categoryId;
    }
    let isGlobalCategoryIdMatched = true;
    if (!_.isEmpty(globalCategoryId) && _.has(ratingObj, "globalCategoryId")) {
      isGlobalCategoryIdMatched =
        ratingObj.globalCategoryId === globalCategoryId;
    }
    return (
      isMatched &&
      ratingObj.academicCriteriaSet == setId &&
      computedRatingCheck &&
      ratingObj.nodeId == nodeId &&
      isTermIdMatched &&
      isCategoryIdMatched &&
      isGlobalCategoryIdMatched
    );
  });
};

export const getStudentElementRatingValueOfCriteriaSet = params => {
  const rating = getStudentElementRatingOfCriteriaSet(params);
  if (_.get(params, "returnIsRemoved", false)) {
    return {
      valueId: _.get(rating, "academicCriteriaValue", null),
      isRemoved: _.get(rating, "isRemoved", null),
    };
  }
  return params?.isDPProjectCriteria
    ? _.get(rating, "value", null)
    : _.get(rating, "academicCriteriaValue", null);
};

export const transformGradeScaleValuesToCriteriaValues = gradeScaleValues => {
  const transformedValues = _.map(gradeScaleValues, (value, index) => {
    const gradeScaleValueScores = _.get(value, "gradeScaleValueScores", []);
    const firstScore = _.first(gradeScaleValueScores);

    const minValue = _.get(firstScore, "minValue");
    const maxValue = _.get(firstScore, "maxValue");
    let boundary_guidelines = [];

    if (!_.isNil(minValue) && !_.isNil(maxValue)) {
      const start = Math.floor(minValue);
      const end = Math.ceil(maxValue);
      const endRange = index == 0 ? end + 1 : end;
      boundary_guidelines = _.range(start, endRange);
    }

    return {
      id: _.get(value, "id"),
      abbreviation: _.get(value, "abbreviation", "-"),
      label: _.get(value, "label", ""),
      description: _.get(value, "description", ""),
      displaySequence: _.get(value, "displaySequence", 0),
      color: _.get(value, "color", ""),
      boundary_guidelines,
    };
  });

  return transformedValues;
};

// For now score aggregation is enabled in cumulative report section view only

export const getRatingIndex = ({
  isComputedRating = true,
  ratings,
  academicCriteriaSetId,
  ibPYPElementId,
  categoryId = null,
  gradingPeriodType,
  academicTermId,
}) => {
  return _.findIndex(ratings, rating => {
    return (
      academicCriteriaSetId == _.get(rating, "academicCriteriaSet", "") &&
      ibPYPElementId == _.get(rating, "ibPYPElementId", "") &&
      isComputedRating == _.get(rating, "isComputedRating", "") &&
      categoryId == _.get(rating, "categoryId", null) &&
      (gradingPeriodType == "CUMULATIVE"
        ? academicTermId == rating.academicTermId
        : true)
    );
  });
};

// This common fn is used to find the index of matching rating for gpa associated ratings (gpRatings, credits, qualityPoints and gpaRatings)
// Since gpa is not defined at a single subject level (it's calculated based on multiple subjects), so in that case subjectId is not passed

export const getGpaAssociatedRatingIndex = ({
  ratings,
  subjectId,
  academicTermId,
}) => {
  return _.findIndex(ratings, rating => {
    return (
      (subjectId ? subjectId == _.get(rating, "subjectId") : true) &&
      academicTermId == _.get(rating, "academicTermId")
    );
  });
};

export const getCalculatedRatingIndex = ({
  ratings,
  academicCriteriaSetId,
  ibPYPElementId,
  categoryId,
  curriculumType,
  gradingPeriodType,
  academicTermId,
}) => {
  return _.findIndex(ratings, item => {
    const isNotNewId =
      curriculumType == CURRICULUM_TYPE_MYP || !_.includes(item.id, "NEW");
    const isMatchingTerm =
      gradingPeriodType != "CUMULATIVE" ||
      item.academicTermId == academicTermId;
    return (
      item.academicCriteriaSet == academicCriteriaSetId &&
      item.ibPYPElementId == ibPYPElementId &&
      item.categoryId == categoryId &&
      item.isComputedRating &&
      item.academicTermId &&
      // Final grade can be calculated from criterions which might have ID new
      isNotNewId &&
      isMatchingTerm
    );
  });
};

export const getAcademicCriteriaValueFromScoreValues = ({
  academicCriteriaValues,
  score,
}) => {
  // Sort the academicCriteriaValues based on their weightage in descending order
  const sortedValuesBasedOnScoreValues = _.sortBy(
    academicCriteriaValues,
    value => {
      return -_.toNumber(_.get(value, ["scoreValues", 0, "minValue"]));
    }
  );

  // Find the first criteriaValue with minValue less than equal to score
  const mappedAcademicCriteriaValue = _.find(
    sortedValuesBasedOnScoreValues,
    item => {
      const scoreValue = _.first(item.scoreValues);
      return scoreValue.minValue <= score;
    }
  );

  return mappedAcademicCriteriaValue;
};

export const getFinalGradeBoundaryCriteriaSet = ({
  academicCriteriaSets,
  subjectId,
  gradeId,
  gradingPeriodType,
  academicTermId,
}) => {
  const finalGradeBoundaryCriteriaSet = _.find(
    academicCriteriaSets,
    criteriaSet => {
      if (
        !(
          criteriaSet?.criteriaType == "GRADE_BOUNDARY" &&
          _.includes(
            ["IB_DEFINED_TERM_GRADE_BOUNDARY", "CUSTOM_TERM_GRADE_BOUNDARY"],
            criteriaSet?.subType
          )
        )
      ) {
        return false;
      }

      const {
        subjectEntityId,
        termEntityId,
        gradeEntityId,
      } = getSubjectTermAndGradeEntityIdFromParentEntities({ criteriaSet });

      return (
        subjectEntityId == subjectId &&
        gradeEntityId == gradeId &&
        (gradingPeriodType == "CUMULATIVE"
          ? academicTermId == termEntityId
          : true)
      );
    }
  );

  return finalGradeBoundaryCriteriaSet;
};
